/**
 * Chapter022.js — Electrical Equipment Register (OHSE 022)
 * Migrated from SWMS Platform Chapter022.tsx → JavaScript
 *
 * Props:
 *   data        {array}   electrical_equipment_register from swmsData
 *   onChange    {fn}      (newEntries) => void
 *   companyName {string}  from companyInfo.name
 *   projectName {string}  from projectInfo.project_name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

function emptyEntry() {
  return {
    id: crypto.randomUUID(),
    equipment_description: '',
    plant_serial_no: '',
    date_of_inspection: '',
    trip_current_eld: '',
    date_of_next_inspection: '',
    electrician_signature: '',
    license_registration_no: '',
  };
}

export default function Chapter022({ data = [], onChange, companyName, projectName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => {
    onChange([...data, emptyEntry()]);
  }, [data, onChange]);

  const removeEntry = useCallback((id) => {
    onChange(data.filter(e => e.id !== id));
  }, [data, onChange]);

  const updateEntry = useCallback((id, field, value) => {
    onChange(data.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 022</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Electrical Equipment Register
        </h2>
      </div>

      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Electrical Equipment Register
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Record all electrical equipment brought on site</p>
        </div>

        <div className="p-4">
          {/* Declaration */}
          <p className="text-sm text-[#1a1a2e] leading-relaxed mb-1">
            <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
            records all electrical equipment brought on site in the Electrical Equipment Register.
          </p>
          <p className="text-xs text-gray-400 italic mb-4">
            Note: Testing and Tagging frequency is as required by State or Territory Legislation, codes and relevant standards.
          </p>

          {/* Workplace read-only */}
          {projectName && (
            <div className="mb-4 text-sm text-gray-500">
              <span className="font-medium text-[#1a1a2e]">Workplace: </span>{projectName}
            </div>
          )}

          {/* Entries */}
          {data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <p className="text-sm text-gray-400 mb-3">No electrical equipment registered yet.</p>
              <button type="button" onClick={addEntry}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
                <Plus className="w-4 h-4" /> Add Equipment
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {data.map((entry) => (
                <div key={entry.id} className="border border-[#e0dbd0] rounded-lg p-4 group relative">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-[#1a1a2e]">
                      {entry.equipment_description || 'New Equipment'}
                    </span>
                    <button type="button" onClick={() => removeEntry(entry.id)}
                      className="p-1 text-red-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Equipment Description">
                      <input type="text" value={entry.equipment_description || ''} placeholder="e.g. Power drill, Extension lead"
                        onChange={e => updateEntry(entry.id, 'equipment_description', e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Plant / Serial No.">
                      <input type="text" value={entry.plant_serial_no || ''} placeholder="Serial or plant number"
                        onChange={e => updateEntry(entry.id, 'plant_serial_no', e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Date of Inspection / Test">
                      <input type="date" value={entry.date_of_inspection || ''}
                        onChange={e => updateEntry(entry.id, 'date_of_inspection', e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Results / Trip Current (ELD less 30mA)">
                      <input type="text" value={entry.trip_current_eld || ''} placeholder="e.g. Pass / Fail / N/A"
                        onChange={e => updateEntry(entry.id, 'trip_current_eld', e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Date of Next Inspection / Test">
                      <input type="date" value={entry.date_of_next_inspection || ''}
                        onChange={e => updateEntry(entry.id, 'date_of_next_inspection', e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Electrician's / Qualified Person's Signature">
                      <input type="text" value={entry.electrician_signature || ''} placeholder="Name of electrician"
                        onChange={e => updateEntry(entry.id, 'electrician_signature', e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="License / Registration No.">
                      <input type="text" value={entry.license_registration_no || ''} placeholder="License or registration number"
                        onChange={e => updateEntry(entry.id, 'license_registration_no', e.target.value)} className={inputCls} />
                    </Field>
                  </div>
                </div>
              ))}
              <div className="flex justify-center mt-2">
                <button type="button" onClick={addEntry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
                  <Plus className="w-4 h-4" /> Add Electrical Equipment
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
